import React from 'react'
import PropTypes from 'prop-types'
import {
  Descriptions,
  Typography,
  DatePicker,
  Select,
  Button,
  Space,
  Table,
  Tag,
  Tooltip,
  message
} from 'antd'
import { EditOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { getMonitoringAlarms } from '@/service/alerts'
import {
  pickMonitoringAlarmList,
  getMessageMapFirstThumbnailUrl,
  getMessageMapFirstFullImageUrl
} from '@/views/ys/share/monitoringAlarmThumbUtils'
import { renderTextStr, renderPhoneStr, formatDateTimeStr } from '@/utils/renderUtils'
import SimpleModal from '@/components/SimpleModal/Modal'
import UpdateYingshiDeviceModal from '@/views/ys/share/UpdateYingshiDeviceModal'
import './YsDeviceDrawerBody.less'

const { Text, Paragraph } = Typography
const { RangePicker } = DatePicker

const defaultPageSize = 20

const MONITORING_JAVA_LEVEL_MAP = {
  LOW: { text: '低', color: 'green' },
  MEDIUM: { text: '中', color: 'gold' },
  HIGH: { text: '高', color: 'orange' },
  CRITICAL: { text: '严重', color: 'red' },
  1: { text: '低', color: 'green' },
  2: { text: '中', color: 'gold' },
  3: { text: '高', color: 'orange' },
  4: { text: '严重', color: 'red' }
}

function getJavaAlarmMessageMapSummary (record) {
  const s = record?.messageMap?.summary
  return typeof s === 'string' && s.trim() !== '' ? s.trim() : ''
}

function monitoringJavaRowKey (record) {
  if (record.id != null && record.id !== '') return `java-alarm-${record.id}`
  return `java-alarm-${record.createdAt ?? ''}-${record.type ?? ''}-${record.deviceId ?? ''}`
}

/**
 * 单路设备历史：getMonitoringAlarms 按 设备+通道+时间+处理状态；
 * 详情区可编辑业务字段（PUT /monitoringdevice/{id}）。
 */
export default class YsDeviceDrawerBody extends React.Component {
  static propTypes = {
    device: PropTypes.object,
    storeOptions: PropTypes.array,
    onDeviceUpdated: PropTypes.func
  }

  static defaultProps = {
    device: {},
    storeOptions: []
  }

  constructor (props) {
    super(props)
    this.state = {
      monitoringRange: [dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day')],
      monitoringIsProcessed: null,
      monitoringList: [],
      monitoringLoading: false
    }
  }

  getDeviceId () {
    const { deviceSerial } = this.props.device
    return deviceSerial != null ? String(deviceSerial).trim() : ''
  }

  getChannelParam () {
    const { channelNo } = this.props.device
    if (channelNo == null || channelNo === '') return ''
    return String(channelNo).trim()
  }

  canQuery = () => {
    const d = this.getDeviceId()
    const c = this.getChannelParam()
    return Boolean(d && c)
  }

  fetchMonitoringAlarms = async () => {
    if (!this.canQuery()) {
      return
    }
    const { monitoringRange, monitoringIsProcessed } = this.state
    const range = Array.isArray(monitoringRange) && monitoringRange[0] && monitoringRange[1] ? monitoringRange : null
    if (!range) {
      message.warning('请选择起止时间')
      return
    }
    const start = range[0].valueOf()
    const end = range[1].valueOf()
    if (end <= start) {
      message.warning('结束时间须晚于开始时间')
      return
    }
    this.setState({ monitoringLoading: true })
    try {
      const res = await getMonitoringAlarms({
        deviceId: this.getDeviceId(),
        channelNo: this.getChannelParam(),
        startTime: start,
        endTime: end,
        isProcessed: monitoringIsProcessed
      })
      this.setState({ monitoringList: pickMonitoringAlarmList(res), monitoringLoading: false })
    } catch (e) {
      const errMsg = e?.response?.data?.message || e?.message || '拉取监控告警失败'
      message.error(errMsg)
      this.setState({ monitoringList: [], monitoringLoading: false })
    }
  }

  formatJavaMonitoringLevel = (level) => {
    if (level == null || level === '') {
      return { text: '—', color: 'default' }
    }
    const raw =
      typeof level === 'object' && level != null && !Array.isArray(level)
        ? level.name || level.value
        : level
    const k = raw != null && raw !== '' ? String(raw).trim() : ''
    return (
      (k && MONITORING_JAVA_LEVEL_MAP[k]) || {
        text: k ? renderTextStr(k) : '—',
        color: 'default'
      }
    )
  }

  componentDidMount () {
    if (this.canQuery()) {
      this.fetchMonitoringAlarms()
    }
  }

  componentDidUpdate (prevProps) {
    if (
      prevProps.device.deviceSerial !== this.props.device.deviceSerial ||
      prevProps.device.channelNo !== this.props.device.channelNo
    ) {
      if (this.canQuery()) {
        this.fetchMonitoringAlarms()
      } else {
        this.setState({ monitoringList: [] })
      }
    }
  }

  renderToolbar () {
    const { monitoringRange, monitoringIsProcessed, monitoringLoading } = this.state
    return (
      <div className="ys-device-drawer-body__toolbar">
        <div className="ys-device-drawer-body__device-hint">
          设备 {renderTextStr(this.getDeviceId())} / 通道 {renderTextStr(this.getChannelParam())}
        </div>
        <Space wrap size={[8, 8]} align="center">
          <span className="ys-device-drawer-body__field-label">时间</span>
          <RangePicker
            showTime
            allowClear={false}
            value={monitoringRange}
            onChange={(dates) => this.setState({ monitoringRange: dates || [] })}
            className="ys-device-drawer-body__range"
          />
          <span className="ys-device-drawer-body__field-label">处理状态</span>
          <Select
            className="ys-device-drawer-body__select-proc"
            value={
              monitoringIsProcessed === null || monitoringIsProcessed === undefined
                ? 'all'
                : monitoringIsProcessed
                  ? 'yes'
                  : 'no'
            }
            onChange={(v) => {
              const next = v === 'all' ? null : v === 'yes'
              this.setState({ monitoringIsProcessed: next })
            }}
            options={[
              { value: 'all', label: '全部' },
              { value: 'yes', label: '已处理' },
              { value: 'no', label: '未处理' }
            ]}
          />
          <Button type="primary" loading={monitoringLoading} onClick={this.fetchMonitoringAlarms}>
            查询
          </Button>
        </Space>
      </div>
    )
  }

  renderTable () {
    const { monitoringList, monitoringLoading } = this.state
    const columns = [
      {
        title: '级别',
        dataIndex: 'level',
        key: 'level',
        width: 64,
        render: (lv) => {
          const { text, color } = this.formatJavaMonitoringLevel(lv)
          return <Tag color={color}>{text}</Tag>
        }
      },
      {
        title: '图',
        key: 'thumbnail',
        width: 64,
        render: (_, record) => {
          const thumb = getMessageMapFirstThumbnailUrl(record)
          const full = getMessageMapFirstFullImageUrl(record)
          const preview = thumb || full
          if (!preview) return '—'
          return (
            <SimpleModal baseProps={{ url: full || preview }}>
              <img
                src={preview}
                alt=""
                className="ys-device-drawer-body__thumb"
              />
            </SimpleModal>
          )
        }
      },
      {
        title: '已处理',
        dataIndex: 'isProcessed',
        key: 'isProcessed',
        width: 72,
        render: (v) =>
          v === true ? <Tag color="green">是</Tag> : v === false ? <Tag color="red">否</Tag> : '—'
      },
      {
        title: '备注',
        dataIndex: 'remark',
        key: 'remark',
        ellipsis: true,
        render: (text) => {
          const displayText = renderTextStr(text)
          return (
            <Tooltip title={displayText} placement="topLeft">
              <span>{displayText}</span>
            </Tooltip>
          )
        }
      },
      {
        title: '创建时间',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 150,
        render: (t) => formatDateTimeStr(t)
      }
    ]
    return (
      <Table
        className="ys-device-drawer-body__table"
        tableLayout="fixed"
        loading={monitoringLoading}
        columns={columns}
        dataSource={monitoringList}
        rowKey={monitoringJavaRowKey}
        pagination={
          monitoringList.length > defaultPageSize
            ? { pageSize: defaultPageSize, size: 'small', showTotal: (total) => `共 ${total} 条` }
            : false
        }
        size="small"
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: (record) => {
            const summaryText = getJavaAlarmMessageMapSummary(record)
            return (
              <div className="ys-device-drawer-body__expand">
                {summaryText ? (
                  <div className="ys-device-drawer-body__expand-summary">
                    <div className="ys-device-drawer-body__expand-label">说明</div>
                    <div className="ys-device-drawer-body__expand-text">{renderTextStr(summaryText)}</div>
                  </div>
                ) : null}
                <div className="ys-device-drawer-body__expand-label">原始</div>
                <pre className="ys-device-drawer-body__expand-pre">
                  {JSON.stringify(
                    { messageMap: record?.messageMap, deviceId: record?.deviceId, channelNo: record?.channelNo },
                    null,
                    2
                  )}
                </pre>
              </div>
            )
          },
          rowExpandable: () => true
        }}
      />
    )
  }

  renderDeviceInfo () {
    const { device, storeOptions, onDeviceUpdated } = this.props
    const {
      id,
      name,
      deviceSerial,
      channelNo,
      source,
      trustCategory,
      status,
      storeId,
      storeName,
      brand,
      location,
      bedNo,
      isPinned,
      needInference,
      notifyPhone,
      remark,
      imageUrl,
      sort
    } = device
    const pinnedOn = isPinned === true || isPinned === 1
    const inferenceOn = needInference === true || needInference === 1
    const canEdit = id != null && id !== ''
    const storeLabel =
      (storeName && String(storeName).trim()) ||
      (storeId != null && storeId !== ''
        ? (storeOptions || []).find((o) => String(o.id) === String(storeId))?.value ||
          `店铺#${storeId}`
        : '')

    return (
      <>
        <div className="ys-device-drawer-body__title-row">
          <Paragraph strong className="ys-device-drawer-body__name">
            {name}
          </Paragraph>
          {canEdit ? (
            <UpdateYingshiDeviceModal
              baseProps={{
                device,
                storeOptions
              }}
              onSuccess={onDeviceUpdated}
            >
              <Button type="link" size="small" icon={<EditOutlined />}>
                编辑信息
              </Button>
            </UpdateYingshiDeviceModal>
          ) : null}
        </div>
        <div className="ys-device-drawer-body__info-body">
          <Descriptions
            size="small"
            column={2}
            bordered
            className="ys-device-drawer-body__desc"
          >
            <Descriptions.Item label="序列号">
              <Text code copyable>
                {deviceSerial}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="通道号">{channelNo}</Descriptions.Item>
            <Descriptions.Item label="门店">{renderTextStr(storeLabel)}</Descriptions.Item>
            <Descriptions.Item label="品牌">{renderTextStr(brand)}</Descriptions.Item>
            <Descriptions.Item label="安装位置">{renderTextStr(location)}</Descriptions.Item>
            <Descriptions.Item label="床号">{renderTextStr(bedNo)}</Descriptions.Item>
            <Descriptions.Item label="通知手机号">{renderPhoneStr(notifyPhone)}</Descriptions.Item>
            <Descriptions.Item label="置顶">
              {pinnedOn ? <Tag color="gold">是</Tag> : <Tag>否</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="推理">
              {inferenceOn ? <Tag color="processing">开启</Tag> : <Tag>关闭</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="排序">
              {sort != null && sort !== '' && Number.isFinite(Number(sort))
                ? String(Number(sort))
                : renderTextStr(null)}
            </Descriptions.Item>
            <Descriptions.Item label="备注">{renderTextStr(remark)}</Descriptions.Item>
            <Descriptions.Item label="来源">
              {source === 'trust' ? '托管' : '账号'}
              {source === 'trust' && trustCategory ? `（${trustCategory}）` : null}
            </Descriptions.Item>
            <Descriptions.Item label="是否在线">
              <Tag color={status === 1 || status === '1' ? 'success' : status === 0 || status === '0' ? 'error' : 'default'}>
                {status === 1 || status === '1' ? '在线' : status === 0 || status === '0' ? '离线' : '—'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
          <div className="ys-device-drawer-body__photo">
            {imageUrl ? (
              <SimpleModal baseProps={{ url: imageUrl }}>
                <button
                  type="button"
                  className="ys-device-drawer-body__photo-btn"
                  aria-label="查看设备图片"
                >
                  <img src={imageUrl} alt="" />
                </button>
              </SimpleModal>
            ) : (
              <span className="ys-device-drawer-body__photo-empty">暂无</span>
            )}
          </div>
        </div>
      </>
    )
  }

  render () {
    return (
      <div className="ys-device-drawer-body">
        {this.renderDeviceInfo()}
        {this.renderToolbar()}
        {this.renderTable()}
      </div>
    )
  }
}
